import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserState } from "./types";
import { fetchUser, fetchUserHasCard, fetchUserTransactionAggregates, fetchUserTransactions } from "./user-thunk";
import { ErrorType } from "@/shared/types";

const currentDate = new Date()

const initialState: UserState = {
    /* page */
    tabIndex: 0,

    /* user */
    user: {
        id: "",
        username: "",
        name: "",
        email: "",
        phone: "",
        date_of_birth: "",
        gender: "male",
        balance: 0,
        values: {},
        date_of_registration: "",
        telegram_id: "",
        is_deleted: false,
        deleted_at: null,
        group: {
            name: ""
        },
        group_id: "",
        role_id: "users",
        role: {
            name: ""
        }
    },
    isUserLoading: false,
    userError: null,
    latestUserRequestId: null,
    latestUserRequestArg: null,
    routeUserId: null,

    /* transactions */
    transactions: [],
    stores: {},
    checks: {},
    transactionsError: null,
    isTransactionsLoading: false,
    transactionsFirstLoad: true,
    page: 1,
    limit: 50,
    totalPages: 0,

    /* aggregates */
    aggregates: {
        bonuses: {},
        items: [],
        count: 0,
        sum_amount_nano: 0
    },
    aggregatesFirstLoad: true,
    aggregatesError: null,
    isAggregatesLoading: false,
    startDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),

    /* user cards */
    hasCard: false,
    isCardLoading: false,
    cardError: null,
    cardFirstLoad: true,
}

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        nextPage: (state) => {
            if (state.page < state.totalPages) {
                state.page += 1
            }
        },
        setTabIndex: (state, action: PayloadAction<number>) => {
            state.tabIndex = action.payload
        },
        setStartDate: (state, action: PayloadAction<Date>) => {
            state.startDate = action.payload
        },
        setEndDate: (state, action: PayloadAction<Date>) => {
            state.endDate = action.payload
        },
        setRouteUserId: (state, action: PayloadAction<string | null>) => {
            state.routeUserId = action.payload
            // The open route owns the store: a GET for another profile can no
            // longer replace the displayed user or hold the loading flag.
            if (action.payload && state.latestUserRequestArg && state.latestUserRequestArg !== action.payload) {
                state.latestUserRequestId = null
                state.latestUserRequestArg = null
                state.isUserLoading = false
            }
        },
        setUserGroup: (state, action: PayloadAction<{ userId: string, groupId: string | null, groupName: string | null }>) => {
            const { userId, groupId, groupName } = action.payload
            if (state.user.id !== userId) return
            state.user.group_id = groupId
            state.user.group = groupId && groupName ? { name: groupName } : null
            // A GET for this user started before the mutation cannot overwrite the confirmed
            // selection. An in-flight GET for another profile keeps its guard untouched.
            if (state.latestUserRequestArg === userId) {
                state.latestUserRequestId = null
                state.latestUserRequestArg = null
            }
        }
    },
    extraReducers(builder) {
        builder
        .addCase(fetchUser.pending, (state, action) => {
            state.isUserLoading = !state.user.id || state.user.id !== action.meta.arg
            state.userError = null
            state.latestUserRequestId = action.meta.requestId
            state.latestUserRequestArg = action.meta.arg
        })
        .addCase(fetchUser.fulfilled, (state, action) => {
            if (state.latestUserRequestId !== action.meta.requestId) return
            state.latestUserRequestId = null
            state.latestUserRequestArg = null
            state.page = 1
            state.transactions = []
            state.stores = {}
            state.checks = {}
            state.aggregates = {
                bonuses: {},
                items: [],
                count: 0,
                sum_amount_nano: 0
            },
            state.aggregatesFirstLoad = true
            state.transactionsError = null
            state.totalPages = 0
            state.userError = null
            state.isUserLoading = false
            state.transactionsFirstLoad = true
            state.user = action.payload
            state.cardFirstLoad = true
        })
        .addCase(fetchUser.rejected, (state, action) => {
            if (state.latestUserRequestId !== action.meta.requestId) return
            state.latestUserRequestId = null
            state.latestUserRequestArg = null
            state.isUserLoading = false
            state.userError = action.payload as ErrorType ?? null
        })
        .addCase(fetchUserTransactions.pending, (state) => {
            state.isTransactionsLoading = true
            state.transactionsError = null
        })
        .addCase(fetchUserTransactions.fulfilled, (state, action) => {
            state.isTransactionsLoading = false
            state.transactions = [...state.transactions, ...action.payload.transactions]
            state.stores = {...state.stores, ...action.payload.stores}
            state.checks = {...state.checks, ...action.payload.checks}
            state.transactionsError = null
            state.transactionsFirstLoad = false
            state.totalPages = Math.ceil(action.payload.total_count / state.limit)
        })
        .addCase(fetchUserTransactions.rejected, (state, action) => {
                state.transactionsError = action.payload as ErrorType ?? null
                state.isTransactionsLoading = false
        })
        .addCase(fetchUserTransactionAggregates.pending, (state) => {
            state.isAggregatesLoading = true
            state.aggregatesError = null
        })
        .addCase(fetchUserTransactionAggregates.fulfilled, (state, action) => {
            state.isAggregatesLoading = false
            state.aggregates = action.payload
            state.aggregatesError = null
            state.aggregatesFirstLoad = false
        })
        .addCase(fetchUserTransactionAggregates.rejected, (state, action) => {
            state.aggregatesError = action.payload as ErrorType ?? null
            state.isAggregatesLoading = false
        })
        .addCase(fetchUserHasCard.pending, (state) => {
            state.isCardLoading = true
            state.cardError = null
        })
        .addCase(fetchUserHasCard.fulfilled, (state, action) => {
            state.isCardLoading = false
            state.hasCard = action.payload.has_card
            state.cardError = null
            state.cardFirstLoad = false
        })
        .addCase(fetchUserHasCard.rejected, (state, action) => {
            state.cardError = action.payload as ErrorType ?? null
            state.isCardLoading = false
        })
    },
})

export default userSlice.reducer
export const { nextPage, setTabIndex, setEndDate, setStartDate, setUserGroup, setRouteUserId } = userSlice.actions